import { Button } from "@/components/ui/button";
import CountrySelect from "@/components/ui/country-select";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

export interface Address {
  key?: string;
  addressLine1: string;
  postcode: string;
  city: string;
  addressLine2?: string;
  country: string;
}

interface AddressListProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  label?: string;
  description?: string;
  maxAddresses?: number;
}

export function AddressList<T extends FieldValues>({
  form,
  name,
  label = "Adressen",
  description,
  maxAddresses = Number.POSITIVE_INFINITY,
}: AddressListProps<T>) {
  const { t } = useTranslation();
  const addresses = form.watch(name) as Address[];

  useEffect(() => {
    const currentAddresses = form.getValues(name) as Address[];
    const hasAddressWithoutKey = currentAddresses.some((addr) => !addr.key);

    if (hasAddressWithoutKey) {
      const addressesWithKeys = currentAddresses.map((addr) =>
        addr.key ? addr : { ...addr, key: crypto.randomUUID() },
      );
      form.setValue(name, addressesWithKeys as PathValue<T, Path<T>>);
    }
  }, [form, name]);

  const addAddress = () => {
    const currentAddresses = form.getValues(name) as Address[];
    if (currentAddresses.length >= maxAddresses) return;

    form.setValue(name, [
      ...currentAddresses,
      {
        key: crypto.randomUUID(),
        addressLine1: "",
        postcode: "",
        city: "",
        addressLine2: "",
        country: "DE",
      },
    ] as PathValue<T, Path<T>>);
  };

  const removeAddress = (index: number) => {
    const currentAddresses = [...(form.getValues(name) as Address[])];
    currentAddresses.splice(index, 1);
    form.setValue(name, currentAddresses as PathValue<T, Path<T>>);
  };

  return (
    <div>
      {label && <h3 className="font-medium mb-2">{label}</h3>}
      {description && (
        <p className="text-sm text-gray-500 mb-6">{description}</p>
      )}

      <FormField
        control={form.control}
        name={name}
        render={() => (
          <FormItem>
            <div className="space-y-4">
              {addresses.map((address, index) => (
                <div
                  key={address.key}
                  className="border rounded-lg p-4 space-y-4 bg-white"
                >
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium text-sm">Adresse {index + 1}</h4>
                    {addresses.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAddress(index)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Entfernen
                      </Button>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name={`${name}.${index}.addressLine1` as Path<T>}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("basic_data.address_fields.street_and_number")}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`${name}.${index}.addressLine2` as Path<T>}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("basic_data.address_fields.additional")}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name={`${name}.${index}.postcode` as Path<T>}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("basic_data.address_fields.postcode")}
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`${name}.${index}.city` as Path<T>}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("basic_data.address_fields.city")}
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name={`${name}.${index}.country` as Path<T>}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("basic_data.address_fields.country")}
                        </FormLabel>
                        <FormControl>
                          <CountrySelect
                            value={field.value || "DE"}
                            onSelect={(value: string) => field.onChange(value)}
                            selectedCountryCode={field.value || "DE"}
                            onCountryChange={(value: string) =>
                              field.onChange(value)
                            }
                            searchable={false}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ))}

              {addresses.length < maxAddresses && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={addAddress}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Weitere Adresse hinzufügen
                </Button>
              )}
            </div>
          </FormItem>
        )}
      />
    </div>
  );
}
